/* eslint-disable no-console */
import { z } from 'zod';

import UserSchema from '@/modules/auth/userSchema';
import mixpanel from '@/utils/mixpanel';

export type AnalyticsEvent =
  | 'page_view'
  | 'button_click'
  | 'card_interaction'
  | 'filter_change'
  | 'search'
  | 'select'
  | 'driver_search'
  | 'selected_driver'
  | 'login';

export type IncrementMetric =
  | '# of Filters by Datetime'
  | '# of Location Filters Applied'
  | '# of Trip Period Filters Applied'
  | '# of Performance Filters Applied'
  | '# of Tag Filters Applied'
  | '# of Fleet Summary Reports Requested'
  | '# of Logout'
  | '# of Driver searches'
  | '# of Driver Summary Reports Requested'
  | '# of Login';

export type AnalyticsProperties = {
  page?: string;
  component?: string;
  action?: string;
  category?: string;
  label?: string;
  value?: number;
  [key: string]: string | number | boolean | undefined;
};

export type UserProperties = {
  $email?: string;
  $name?: string;
  [key: string]: string | number | boolean | undefined;
};

type User = z.infer<typeof UserSchema>;

export type AnalyticsUser = {
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  applicationId: string | number;
  roles: string[];
};

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string;

export const useAnalytics = () => {
  const trackEvent = (
    eventName: AnalyticsEvent,
    properties?: AnalyticsProperties
  ) => {
    if (!MIXPANEL_TOKEN) {
      console.warn('Mixpanel token not found. Analytics tracking is disabled.');
      return;
    }

    try {
      mixpanel.track(eventName, {
        timestamp: new Date().toISOString(),
        ...properties,
      });
    } catch (error) {
      console.error('Error tracking analytics event:', error);
    }
  };

  const identify = (userId: string) => {
    if (!MIXPANEL_TOKEN) return;

    try {
      mixpanel.identify(userId);
    } catch (error) {
      console.error('Error identifying user:', error);
    }
  };

  const setUserProperties = (properties: UserProperties) => {
    if (!MIXPANEL_TOKEN) return;

    try {
      mixpanel.people.set(properties);
    } catch (error) {
      console.error('Error setting user properties:', error);
    }
  };

  const setGroup = (groupType: string, groupNames: string[]) => {
    if (!MIXPANEL_TOKEN) return;

    try {
      mixpanel.set_group(groupType, groupNames);
    } catch (error) {
      console.error('Error setting group:', error);
    }
  };

  const setGroupProperties = (
    groupType: string,
    groupName: string,
    properties: Record<string, string | number | boolean>
  ) => {
    if (!MIXPANEL_TOKEN) return;

    try {
      mixpanel.get_group(groupType, groupName).set_once(properties);
    } catch (error) {
      console.error('Error setting group properties:', error);
    }
  };

  const registerSuperProperties = (
    properties: Record<string, string | number | boolean>
  ) => {
    if (!MIXPANEL_TOKEN) return;

    try {
      mixpanel.register(properties);
    } catch (error) {
      console.error('Error registering super properties:', error);
    }
  };

  const setupUser = (user: User) => {
    if (!MIXPANEL_TOKEN) {
      console.warn('Mixpanel token not found. Analytics tracking is disabled.');
      return;
    }

    try {
      // Set company/partner group
      const {
        email,
        firstName,
        lastName,
        roles,
        companyName: partnerName = '',
        applicationId: partnerId = '',
      } = user;

      setGroup('partnerName', [partnerName]);
      setGroupProperties('partnerName', partnerName, {
        partnerName,
        partnerId: partnerId ?? '',
      });

      // Identify the user
      identify(email);

      // Set user properties
      setUserProperties({
        $email: email,
        $name: `${firstName} ${lastName}`,
        partnerName,
        partnerId: partnerId ?? '',
      });

      // Register super properties
      registerSuperProperties({
        $email: email,
        $name: `${firstName} ${lastName}`,
        userType: roles[0],
        partnerName,
        partnerId: partnerId ?? '',
      });
    } catch (error) {
      console.error('Error setting up analytics user:', error);
    }
  };

  const incrementUserMetric = (metric: IncrementMetric) => {
    if (!MIXPANEL_TOKEN) {
      console.warn('Mixpanel token not found. Analytics tracking is disabled.');
      return;
    }

    try {
      mixpanel.people.increment(metric);
    } catch (error) {
      console.error('Error incrementing user metric:', error);
    }
  };

  return {
    trackEvent,
    identify,
    setUserProperties,
    setGroup,
    setGroupProperties,
    registerSuperProperties,
    setupUser,
    incrementUserMetric,
  };
};
